import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
// import { FaSearch } from "react-icons/fa"; // Commenting out if not used or verifying package

const MarketplaceSearch = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const wrapperRef = useRef(null);

    // Click outside handler
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    // Debounce search
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (query.trim()) {
                setIsLoading(true);
                try {
                    const response = await fetch(`/api/marketplace/apps?search=${encodeURIComponent(query)}`);
                    if (response.ok) {
                        const data = await response.json();
                        setResults(data);
                        setShowDropdown(true);
                    } else {
                        console.error("Search failed:", response.status);
                    }
                } catch (error) {
                    console.error("Search error:", error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setResults([]);
                setShowDropdown(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [query]);

    const handleSelect = (app) => {
        if (app.slug) {
            navigate(`/marketplace/app/${app.slug}`);
        }
        setShowDropdown(false);
    };

    const handleSearch = () => {
        if (query.trim()) {
            // Open search results in a new tab
            window.open(`/marketplace/search?q=${encodeURIComponent(query.trim())}`, '_blank');
            setShowDropdown(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    return (
        <div className="relative w-full z-50" ref={wrapperRef}>
            <div className="flex items-center mt-6 w-full relative">
                <div className="relative w-full hero-actions">
                    <input
                        type="text"
                        className="input"
                        placeholder="Search apps"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => {
                            if (query.trim()) setShowDropdown(true);
                        }}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                <button
                    onClick={handleSearch}
                    className="hero-actions-button"
                >
                    Search
                </button>
            </div>

            {/* Dropdown */}
            {showDropdown && (results.length > 0 || isLoading) && (
                <div className="absolute top-14 left-0 w-full bg-white shadow-2xl rounded-xl mt-2 max-h-96 overflow-y-auto border border-gray-100 z-[100] transform transition-all duration-200 ease-in-out">
                    {isLoading && results.length === 0 && (
                        <div className="p-6 text-gray-500 text-center flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                            Loading...
                        </div>
                    )}
                    {results.map((app) => (
                        <div
                            key={app.id}
                            className="p-4 hover:bg-blue-50 cursor-pointer flex items-center justify-between border-b border-gray-50 last:border-0 transition-colors duration-200 group"
                            onClick={() => handleSelect(app)}
                        >
                            <div className="flex flex-col text-left">
                                <span className="font-bold text-gray-900 text-base group-hover:text-blue-700 transition-colors">{app.name}</span>
                                <span className="text-sm text-gray-500 mt-1">in <span className="text-gray-700 font-medium">{app.category || "General"}</span></span>
                            </div>
                            {app.icon_url ? (
                                <img src={app.icon_url} alt={app.name} className="w-10 h-10 object-contain rounded-lg shadow-sm bg-white p-1" />
                            ) : (
                                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs">App</div>
                            )}
                        </div>
                    ))}
                    {results.length === 0 && !isLoading && query.trim() && (
                        <div className="p-6 text-gray-500 text-center">
                            No results found for "<span className="font-semibold text-gray-700">{query}</span>"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MarketplaceSearch;
